import { useEffect, useState } from 'react'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import { useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { SkeletonRows } from '../../components/skeleton'
import { StatusFilter, FilterBanner } from './platformFilters'
import PlatformConfirm from './platformConfirm'
import { getUsers, deleteUser, resetUserPassword, deactivateUser, activateUser, updateUser } from '../../services/platform'
import { onQuerySuccess, onMutateSuccess } from '../../utils/queryResponse'
import refreshPlatform from './refreshPlatform'
import { toast } from '../../utils/toast'
import { dateLocale } from '../../i18n/i18n'
import { roleListLabel } from '../../constants/labels'

const AVATAR_COLORS = ['#3B82F6', '#F59E0B', '#10B981', '#8B5CF6', '#EF4444', '#EC4899', '#06B6D4', '#F97316']
const getInitials = (name) => { if (!name) return '?'; const parts = name.trim().split(/\s+/); return parts.length >= 2 ? (parts[0][0] + parts[parts.length - 1][0]).toUpperCase() : name.substring(0, 2).toUpperCase() }
const getColor = (name) => { let h = 0; for (let i = 0; i < (name || '').length; i++) h = name.charCodeAt(i) + ((h << 5) - h); return AVATAR_COLORS[Math.abs(h) % AVATAR_COLORS.length] }

// ?status=active is set by the dashboard's Active Users card or the Filters
// menu; it matches that card's count (signed in at least once).
const STATUSES = ['active']

const PlatformUsersPage = () => {
    const { t } = useTranslation()
    const qc = useQueryClient()
    const [searchParams, setSearchParams] = useSearchParams()
    const status = STATUSES.includes(searchParams.get('status')) ? searchParams.get('status') : ''
    const [page, setPage] = useState(1)
    const [search, setSearch] = useState('')
    const [users, setUsers] = useState([])
    const [total, setTotal] = useState(0)
    const [actionOpen, setActionOpen] = useState(null)
    const [confirm, setConfirm] = useState(null)
    const [editModal, setEditModal] = useState(null)
    const [editForm, setEditForm] = useState({ fullName: '', email: '' })
    const limit = 20

    const { isFetching, data } = useQuery(['platform-users', page, search, status], () => getUsers({ page, limit, search, status }), { keepPreviousData: true })

    const setStatus = (next) => {
        setPage(1)
        setSearchParams(next ? { status: next } : {})
    }

    useEffect(() => { onQuerySuccess(data, (r) => { setUsers(r.rows || []); setTotal(r.total || 0) }) }, [data])

    const deleteMut = useMutation(deleteUser, { onSuccess: onMutateSuccess(() => refreshPlatform(qc), t('platform.userActions.deletedToast')) })
    const resetPwMut = useMutation(resetUserPassword, { onSuccess: onMutateSuccess(() => {}, t('platform.userActions.resetSentToast')) })
    const deactivateMut = useMutation(deactivateUser, { onSuccess: onMutateSuccess(() => refreshPlatform(qc), t('platform.userActions.deactivatedToast')) })
    const activateMut = useMutation(activateUser, { onSuccess: onMutateSuccess(() => refreshPlatform(qc), t('platform.userActions.activatedToast')) })
    const updateMut = useMutation(({ id, payload }) => updateUser(id, payload), { onSuccess: onMutateSuccess(() => { refreshPlatform(qc); setEditModal(null) }, t('platform.userActions.updatedToast')) })

    const totalPages = Math.ceil(total / limit) || 1

    const openEdit = (u) => {
        setEditForm({ fullName: u.fullName, email: u.email || '' })
        setEditModal(u)
        setActionOpen(null)
    }

    const handleEdit = (e) => {
        e.preventDefault()
        if (!editForm.fullName) return toast.error(t('platform.common.nameRequired'))
        updateMut.mutate({ id: editModal.id, payload: editForm })
    }

    // Deactivate and Delete wait for the confirmation modal.
    const askConfirm = (action, u) => {
        setConfirm({ action, id: u.id, name: u.fullName })
        setActionOpen(null)
    }

    const runConfirmed = ({ action, id }) => {
        if (action === 'deactivate') deactivateMut.mutate(id)
        if (action === 'delete') deleteMut.mutate(id)
    }

    return (
        <div className="sbr">
            <div className="pl-page-head" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
                <div>
                    <h2 style={{ fontSize: 22, fontWeight: 700, margin: 0, color: '#0B1424' }}>{t('platform.users.title')}</h2>
                    <p style={{ fontSize: 13, color: '#697586', margin: 0 }}>{t('platform.users.subtitle')}</p>
                </div>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 14 }}>
                <StatusFilter value={status} onChange={setStatus} options={[
                    { value: 'active', label: t('platform.common.active') },
                ]} />
                <div className="p-search-bar" style={{ flex: 1 }}>
                    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="#9CA3B0" strokeWidth="2"><circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" /></svg>
                    <input placeholder={t('platform.users.search')} value={search} onChange={(e) => { setSearch(e.target.value); setPage(1) }} />
                </div>
            </div>

            {status === 'active' && (
                <FilterBanner
                    highlight={t('platform.users.bannerActive', { count: total })}
                    hint={t('platform.users.bannerActiveHint')}
                    onClear={() => setStatus('')}
                />
            )}

            <div className="card-sbr" style={{ padding: 0 }}>
                {isFetching && users.length === 0 ? (
                    <div style={{ padding: 20 }}><SkeletonRows rows={6} widths={['25%', '20%', '15%', '15%', '10%']} /></div>
                ) : (
                    <>
                        <table className="p-table">
                            <thead>
                                <tr>
                                    <th>{t('platform.columns.user')}</th>
                                    <th className="p-col-opt">{t('platform.columns.company')}</th>
                                    <th className="p-col-opt">{t('platform.columns.role')}</th>
                                    <th>{t('platform.columns.status')}</th>
                                    <th className="p-col-opt">{t('platform.columns.lastLogin')}</th>
                                    <th style={{ width: 50 }}></th>
                                </tr>
                            </thead>
                            <tbody>
                                {users.map(u => (
                                    <tr key={u.id}>
                                        <td>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                                <div style={{ width: 34, height: 34, borderRadius: '50%', background: getColor(u.fullName), display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700, color: '#fff', flexShrink: 0 }}>{getInitials(u.fullName)}</div>
                                                <div>
                                                    <div style={{ fontSize: 13, fontWeight: 600, color: '#0B1424' }}>{u.fullName}</div>
                                                    <div style={{ fontSize: 11, color: '#9CA3B0' }}>{u.email || u.username}</div>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="p-col-opt" style={{ fontSize: 12, color: '#697586' }}>{u.companyName || '—'}</td>
                                        <td className="p-col-opt"><span className="p-role-badge">{u.role ? roleListLabel(u.role) : '—'}</span></td>
                                        <td>
                                            <span className={`p-status-badge ${u.isDeactivated ? 'p-status-suspended' : 'p-status-active'}`}>
                                                {u.isDeactivated ? t('platform.common.deactivated') : t('platform.common.active')}
                                            </span>
                                        </td>
                                        <td className="p-col-opt" style={{ fontSize: 12, color: '#697586' }}>
                                            {u.lastLoginAt ? new Date(u.lastLoginAt).toLocaleDateString(dateLocale(), { day: '2-digit', month: 'short', year: 'numeric' }) : t('platform.common.never')}
                                        </td>
                                        <td>
                                            <div style={{ position: 'relative' }}>
                                                <button style={{ background: 'none', border: '1px solid #EBEEF5', borderRadius: 6, padding: '4px 8px', cursor: 'pointer', color: '#697586' }} onClick={() => setActionOpen(actionOpen === u.id ? null : u.id)}>
                                                    <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor"><circle cx="12" cy="5" r="1.5" /><circle cx="12" cy="12" r="1.5" /><circle cx="12" cy="19" r="1.5" /></svg>
                                                </button>
                                                {actionOpen === u.id && (
                                                    <div className="p-action-dropdown">
                                                        <button className="p-action-dropdown-item" onClick={() => openEdit(u)}>
                                                            <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" /><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" /></svg>
                                                            {t('platform.userActions.editDetails')}
                                                        </button>
                                                        <button className="p-action-dropdown-item" onClick={() => { resetPwMut.mutate(u.id); setActionOpen(null) }}>
                                                            <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="11" width="18" height="11" rx="2" ry="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" /></svg>
                                                            {t('platform.userActions.resetPassword')}
                                                        </button>
                                                        <div className="p-action-dropdown-divider" />
                                                        {u.isDeactivated ? (
                                                            <button className="p-action-dropdown-item" onClick={() => { activateMut.mutate(u.id); setActionOpen(null) }}>
                                                                <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="#16A34A" strokeWidth="2"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" /><polyline points="22 4 12 14.01 9 11.01" /></svg>
                                                                {t('platform.userActions.activate')}
                                                            </button>
                                                        ) : (
                                                            <button className="p-action-dropdown-item p-action-dropdown-danger" onClick={() => askConfirm('deactivate', u)}>
                                                                <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10" /><line x1="4.93" y1="4.93" x2="19.07" y2="19.07" /></svg>
                                                                {t('platform.userActions.deactivate')}
                                                            </button>
                                                        )}
                                                        <button className="p-action-dropdown-item p-action-dropdown-danger" onClick={() => askConfirm('delete', u)}>
                                                            <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6" /><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" /></svg>
                                                            {t('platform.userActions.deleteUser')}
                                                        </button>
                                                    </div>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                                {users.length === 0 && !isFetching && (
                                    <tr><td colSpan={6} style={{ textAlign: 'center', color: '#9CA3B0', padding: 32 }}>{t('platform.users.empty')}</td></tr>
                                )}
                            </tbody>
                        </table>

                        {totalPages > 1 && (
                            <div className="p-pagination">
                                <div className="p-pagination-info">{t('platform.common.showing', { from: (page - 1) * limit + 1, to: Math.min(page * limit, total), total })}</div>
                                <div className="p-pagination-controls">
                                    <button className="p-pagination-btn" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>‹</button>
                                    {Array.from({ length: totalPages }, (_, i) => i + 1).slice(Math.max(0, page - 3), page + 2).map(p => (
                                        <button key={p} className={`p-pagination-btn ${p === page ? 'active' : ''}`} onClick={() => setPage(p)}>{p}</button>
                                    ))}
                                    <button className="p-pagination-btn" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>›</button>
                                </div>
                            </div>
                        )}
                    </>
                )}
            </div>

            <PlatformConfirm confirm={confirm} onClose={() => setConfirm(null)} onConfirm={runConfirmed} />

            {/* Edit User Modal */}
            {editModal && (
                <div className="p-modal-overlay" onClick={() => setEditModal(null)}>
                    <div className="p-modal" onClick={e => e.stopPropagation()}>
                        <h3>{t('platform.userActions.editTitle')}</h3>
                        <form onSubmit={handleEdit}>
                            <label>{t('platform.common.fullNameLabel')}</label>
                            <input value={editForm.fullName} onChange={e => setEditForm(f => ({ ...f, fullName: e.target.value }))} />
                            <label>{t('platform.common.emailLabel')}</label>
                            <input type="email" value={editForm.email} onChange={e => setEditForm(f => ({ ...f, email: e.target.value }))} />
                            <div className="p-modal-actions">
                                <button type="button" className="btn-sbr btn-ghost-sbr" onClick={() => setEditModal(null)} style={{ padding: '8px 16px', fontSize: 13, borderRadius: 8 }}>{t('platform.common.cancel')}</button>
                                <button type="submit" className="btn-sbr btn-primary-sbr" disabled={updateMut.isLoading} style={{ padding: '8px 18px', fontSize: 13, borderRadius: 8 }}>{updateMut.isLoading ? t('platform.common.saving') : t('platform.common.save')}</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    )
}

export default PlatformUsersPage
